import React, { useState, useEffect } from 'react'
import qs from 'qs'
import { Alert, Button, Card, Col, Container, Form, InputGroup, Row, Spinner } from 'react-bootstrap'
import { Eye, EyeSlash } from 'react-bootstrap-icons'
import { useFormik } from 'formik'
import ConfigLayout from '../../Layouts/ConfigLayout'
import { PasswordSchema } from '../../../utils/validation'
import { changePasswordUser, verifyToken } from '../../../redux/actions/user'
import { useSelector, useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'

const ChangePassword = () => {
  const [alertState, setAlertState] = useState({ show: true, variant: 'info', message: 'Please set a new password' })
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const dispatch = useDispatch()

  const { user, changePasswordState, verifyTokenState } = useSelector((state) => ({
    user: state.user.user,
    changePasswordState: state.user.changePassword,
    verifyTokenState: state.user.verifyToken
  }))

  useEffect(() => {
    if (changePasswordState.error !== false) {
      setAlertState({
        show: true,
        variant: 'danger',
        message: 'Unable to send change password'
      })
    }

    if (changePasswordState.success) {
      setAlertState({
        show: true,
        variant: 'success',
        message: 'Password has been changed'
      })
    }
  }, [changePasswordState.error, changePasswordState.success])

  const history = useHistory()

  const handleAlertOnClick = () => {
    if (user.userId !== undefined && user.status === 'ACTIVE') {
      history.goBack()
    } else {
      window.location = '/login'
    }
  }

  const querystring = qs.parse(window.location.search.slice(1))
  
  useEffect(() => {
    if (querystring?.user) {
      dispatch(verifyToken(querystring?.user))
    }
  }, [])

  useEffect(() => {
    if (verifyTokenState?.error === true || verifyTokenState?.error.message) {
      window.location = '/forgot-password?Reset=true' 
    }
  }, [verifyTokenState])

  const changePassword = useFormik({
    initialValues: {
      password: '',
      verifyPassword: '',
      userToken: querystring?.user,
      userId: user.userId
    },
    enableReinitialize: true,
    validationSchema: PasswordSchema,
    onSubmit: (values) => {
      dispatch(changePasswordUser(values))
    }
  })

  return (
    <ConfigLayout showUser={user.userId !== undefined && user.status !== 'VALIDATED'}>
      <Container fluid id='login'>
        <Row>
          <Col xs={12} className='mb-3'>
            <Card className='auth-container mx-auto'>
              <Card.Body>
                <Form onSubmit={changePassword.handleSubmit} noValidate>
                  <Row>
                    <Col xs={12}>
                      <h3>Change Password</h3>
                      <Alert 
                        show={alertState.show} 
                        dismissible
                        variant={alertState.variant}
                        onClose={() => setAlertState((ps) => ({ ...ps, show: false }))}
                      >
                        {alertState.message}
                        {
                          changePasswordState.success && (user.userId !== undefined && user.status === 'ACTIVE') ? (
                            <Button size='sm' variant='outline-success' className='ml-3' onClick={handleAlertOnClick}>
                              Go Back
                            </Button>
                          ) : changePasswordState.success && (user.userId !== undefined && user.status === 'VERIFIED') ? (
                            <Button size='sm' variant='outline-success' className='ml-3' onClick={handleAlertOnClick}>
                              Please Login
                            </Button>
                          ) : null
                        }
                      </Alert>
                    </Col>
                  </Row>
                  <Row>
                    <Form.Group as={Col} xs={12} className='mb-3'>
                      <Form.Label>Set New Password</Form.Label>
                      <InputGroup>
                        <Form.Control
                          id='password'
                          name='password'
                          type={showPassword ? 'text' : 'password'}
                          onChange={changePassword.handleChange}
                          onBlur={changePassword.handleBlur}
                          isInvalid={!!changePassword.errors.password && changePassword.touched.password}
                          value={changePassword.values.password}
                          autoComplete='password'
                        />
                        <InputGroup.Append>
                          <Button className='font-85' variant='secondary' onClick={() => setShowPassword((ps) => !ps)}>
                            {showPassword ? <Eye size={14} /> : <EyeSlash size={14} />}
                          </Button>
                        </InputGroup.Append>
                        <Form.Control.Feedback type='invalid'>
                          {changePassword.errors.password}
                        </Form.Control.Feedback>
                      </InputGroup>
                    </Form.Group>
                  </Row>
                  <Row>
                    <Form.Group as={Col} xs={12} className='mb-3'>
                      <Form.Label>Confirm New Password</Form.Label>
                      <InputGroup>
                        <Form.Control
                          id='verifyPassword'
                          name='verifyPassword'
                          type={showConfirmPassword ? 'text' : 'password'}
                          onChange={changePassword.handleChange}
                          onBlur={changePassword.handleBlur}
                          isInvalid={!!changePassword.errors.verifyPassword && changePassword.touched.verifyPassword}
                          value={changePassword.values.verifyPassword}
                          autoComplete='password'
                        />
                        <InputGroup.Append>
                          <Button className='font-85' variant='secondary' onClick={() => setShowConfirmPassword((ps) => !ps)}>
                            {showConfirmPassword ? <Eye size={14} /> : <EyeSlash size={14} />}
                          </Button>
                        </InputGroup.Append>
                        <Form.Control.Feedback type='invalid'>
                          {changePassword.errors.verifyPassword}
                        </Form.Control.Feedback>
                      </InputGroup>
                    </Form.Group>
                    <Col xs={12} className='mb-3'>
                      <Button
                        type='submit'
                        className='w-100 font-85'
                        disabled={changePasswordState.loading}
                      >
                        {
                          changePasswordState.loading
                            ? (
                              <Spinner animation='border' role='status' size='sm' />
                            )
                            : 'Save New Password'
                        }
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </Card.Body>
            </Card>
          </Col>
          {
            user.userId === undefined ? (
              <>
                <Col xs={12} className='text-center mb-3'>
                  <h4>
                    <u><a className='text-muted' href='https://info.pocp.com/lupus_advisor' target='_blank' rel='noreferrer'>About Lupus Advisor</a></u>
                  </h4>
                </Col>
                <Col xs={12} className='text-center'>
                  <div className='text-muted'>
                    Lupus Advisor Configurator is available to current customers of Lupus Advisor.
                  </div>
                </Col>
              </>
            ) : null
          }
        </Row>
      </Container>
    </ConfigLayout>
  )
}

export default ChangePassword
